import React, { useState } from "react";
import {
  Grid as Grid,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Box
} from "@mui/material";
import BuildIdLogo from "../../assets/images/build_id_logo.png";
import { Check, Clear, Visibility, VisibilityOff } from "@mui/icons-material";
import { BIBlue, BIGreen, BIGrey, BIRed } from "../../assets/helpers";
import { useSelector } from "react-redux";
import { English } from "../../constant/EnglishFallback";

export const CreateAccountPage = (props) => {
  const {
    firstName,
    lastName,
    email,
    fnameError,
    lnameError,
    emailError,
    password,
    disableEmail,
    clientLogo,
    organizationName,
    handlFirstNameInput,
    handleLastNameInput,
    handleEmailInput,
    handlePasswordInput,
    handleCreateAccountClick
  } = props;
  const [showPassword, setShowPassword] = useState(false);
  const { multiLanguageData } = useSelector((state) => state?.main);

  const {
    CREATE_ACC_DESC_1 = English?.CreateAccount?.CREATE_ACC_DESC_1,
    CREATE_ACC_DESC_2 = English?.CreateAccount?.CREATE_ACC_DESC_2,
    TEXT_FIRST_NAME = English?.CreateAccount?.TEXT_FIRST_NAME,
    TEXT_LAST_NAME = English?.CreateAccount?.TEXT_LAST_NAME,
    TEXT_EMAIL = English?.CreateAccount?.TEXT_EMAIL,
    TEXT_PASSWORD = English?.CreateAccount?.TEXT_PASSWORD,
    TEXT_PASS_STRENGTH = English?.CreateAccount?.TEXT_PASS_STRENGTH,
    TEXT_STRONG_PASS = English?.CreateAccount?.TEXT_STRONG_PASS,
    TEXT_WEAK_PASS = English?.CreateAccount?.TEXT_WEAK_PASS,
    TEXT_CHARACTERS = English?.CreateAccount?.TEXT_CHARACTERS,
    TEXT_NUMBER = English?.CreateAccount?.TEXT_NUMBER,
    TEXT_CAPITAL_LETTER = English?.CreateAccount?.TEXT_CAPITAL_LETTER,
    TEXT_LOWER_LETTER = English?.CreateAccount?.TEXT_LOWER_LETTER,
    BUTTON_CREATE_ACCOUNT = English?.CreateAccount?.BUTTON_CREATE_ACCOUNT
  } = multiLanguageData?.CreateAccount || {};

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };
  // Validation conditions
  const conditions = {
    length: password.length >= 6,
    number: /\d/.test(password),
    capital: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password)
  };

  const conditionList = [
    { condition: conditions.length, text: TEXT_CHARACTERS },
    { condition: conditions.number, text: TEXT_NUMBER },
    { condition: conditions.capital, text: TEXT_CAPITAL_LETTER },
    { condition: conditions.lowercase, text: TEXT_LOWER_LETTER }
  ];

  return (
    <>
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <img
          src={BuildIdLogo}
          alt="BuildID Logo"
          style={{ display: "block", margin: "0 auto", marginBottom: "10px", maxHeight: "60px" }}
        />
        {organizationName && (
          <Typography
            variant="h6"
            sx={{
              color: BIBlue,
              fontWeight: 600,
              mb: 2,
              fontStyle: "italic",
              fontSize: "1rem"
            }}
          >
            You are invited by {organizationName}
          </Typography>
        )}
        <Typography variant="body1" gutterBottom sx={{ mb: 1 }}>
          {CREATE_ACC_DESC_1}
        </Typography>
        <Typography variant="body1">{CREATE_ACC_DESC_2}</Typography>
      </Box>

      <Box component="form" onSubmit={(e) => { e.preventDefault(); handleCreateAccountClick(); }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              id="outlined-basic"
              placeholder={TEXT_FIRST_NAME}
              value={firstName}
              onChange={handlFirstNameInput}
              variant="outlined"
              error={fnameError}
              helperText={fnameError ? "The FirstName field is required" : ""}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              id="outlined-basic"
              value={lastName}
              onChange={handleLastNameInput}
              placeholder={TEXT_LAST_NAME}
              variant="outlined"
              error={lnameError}
              helperText={lnameError ? "The LastName field is required" : ""}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={handleEmailInput}
              variant="outlined"
              error={emailError}
              disabled={disableEmail}
              helperText={emailError ? "Please enter a valid email address" : ""}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              autoComplete="new-password"
              label={TEXT_PASSWORD}
              type={showPassword ? "text" : "password"}
              variant="outlined"
              value={password}
              onChange={handlePasswordInput}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{ display: "flex", justifyContent: "space-between" }}
            variant="body1"
            gutterBottom>
            <b>{TEXT_PASS_STRENGTH}</b>
            <span
              style={{
                color: Object.values(conditions).every((val) => val) ? BIGreen : BIRed
              }}>
              {Object.values(conditions).every((val) => val) ? TEXT_STRONG_PASS : TEXT_WEAK_PASS}
            </span>
          </Typography>
          <Box
            sx={{
              backgroundColor: "#e0e0e0",
              height: 6,
              borderRadius: 3,
              overflow: "hidden",
              mb: 2
            }}>
            <Box
              sx={{
                backgroundColor: Object.values(conditions).every((val) => val) ? BIGreen : BIRed,
                width: `${Object.values(conditions).filter((val) => val).length * 25}%`,
                height: "100%"
              }}
            />
          </Box>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {conditionList.map(({ condition, text }, index) => {
              const color = password.length === 0 ? BIGrey : condition ? BIGreen : BIRed;
              const Icon = condition || password.length === 0 ? Check : Clear;

              return (
                <li key={index} style={{ color, display: "flex", alignItems: "center" }}>
                  <Icon /> <span style={{ marginLeft: 8 }}>{text}</span>
                </li>
              );
            })}
          </ul>
        </Box>

        <Box sx={{ mt: 4,  textAlign: "center" }} >
          <Button
            variant="contained"
            size="large"
            type="submit"
            fullWidth
            sx={{ maxWidth: { xs: "100%", sm: 400, md: 300 }, background: BIBlue }}
            disabled={!Object.values(conditions).every((val) => val)}>
            {BUTTON_CREATE_ACCOUNT}
          </Button>
        </Box>
      </Box>
    </>
  );
};
